import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSample } from '../SampleContext';
import { useLayout } from '../LayoutContext';

const languages: { code: string; name: string }[] = [
    { code: 'en-US', name: 'English (United States)' },
    { code: 'en-GB', name: 'English (United Kingdom)' },
    { code: 'en-AU', name: 'English (Australia)' },
    { code: 'en-IN', name: 'English (India)' },
    { code: 'es-ES', name: 'Spanish (Spain)' },
    { code: 'es-MX', name: 'Spanish (Mexico)' },
    { code: 'es-AR', name: 'Spanish (Argentina)' },
    { code: 'fr-FR', name: 'French (France)' },
    { code: 'fr-CA', name: 'French (Canada)' },
    { code: 'pt-PT', name: 'Portuguese (Portugal)' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)' },
    { code: 'zh-CN', name: 'Chinese (Simplified)' },
    { code: 'zh-TW', name: 'Chinese (Traditional)' },
    { code: 'de-DE', name: 'German' },
    { code: 'ru-RU', name: 'Russian' },
    { code: 'uk-UA', name: 'Ukrainian' },
    { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
    { code: 'ar-EG', name: 'Arabic (Egypt)' },
    { code: 'ja-JP', name: 'Japanese' },
    { code: 'ko-KR', name: 'Korean' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'bn-IN', name: 'Bengali' },
    { code: 'tr-TR', name: 'Turkish' },
    { code: 'vi-VN', name: 'Vietnamese' },
    { code: 'it-IT', name: 'Italian' },
    { code: 'nl-NL', name: 'Dutch' },
    { code: 'pl-PL', name: 'Polish' },
    { code: 'th-TH', name: 'Thai' },
    { code: 'he-IL', name: 'Hebrew' },
    { code: 'id-ID', name: 'Indonesian' },
    { code: 'ms-MY', name: 'Malay' },
    { code: 'fa-IR', name: 'Persian (Farsi)' },
    { code: 'sv-SE', name: 'Swedish' },
    { code: 'fi-FI', name: 'Finnish' },
    { code: 'no-NO', name: 'Norwegian' },
    { code: 'da-DK', name: 'Danish' },
    { code: 'cs-CZ', name: 'Czech' },
    { code: 'ro-RO', name: 'Romanian' },
    { code: 'el-GR', name: 'Greek' },
    { code: 'hu-HU', name: 'Hungarian' },
    { code: 'ta-IN', name: 'Tamil' },
    { code: 'te-IN', name: 'Telugu' },
    { code: 'mr-IN', name: 'Marathi' },
    { code: 'ur-PK', name: 'Urdu (Pakistan)' },
    { code: 'gu-IN', name: 'Gujarati' },
    { code: 'kn-IN', name: 'Kannada' },
    { code: 'ml-IN', name: 'Malayalam' },
    { code: 'pa-IN', name: 'Punjabi' },
    { code: 'sr-RS', name: 'Serbian' },
    { code: 'hr-HR', name: 'Croatian' },
    { code: 'sk-SK', name: 'Slovak' },
    { code: 'bg-BG', name: 'Bulgarian' },
    { code: 'lt-LT', name: 'Lithuanian' },
    { code: 'lv-LV', name: 'Latvian' },
    { code: 'et-EE', name: 'Estonian' },
    { code: 'sl-SI', name: 'Slovenian' },
    { code: 'af-ZA', name: 'Afrikaans' },
    { code: 'sw-KE', name: 'Swahili' },
    { code: 'am-ET', name: 'Amharic' },
    { code: 'yo-NG', name: 'Yoruba' },
    { code: 'ig-NG', name: 'Igbo' },
    { code: 'zu-ZA', name: 'Zulu' },
    { code: 'xh-ZA', name: 'Xhosa' },
    { code: 'ne-NP', name: 'Nepali' },
    { code: 'si-LK', name: 'Sinhala' },
    { code: 'my-MM', name: 'Burmese' },
    { code: 'km-KH', name: 'Khmer' },
    { code: 'lo-LA', name: 'Lao' },
    { code: 'mn-MN', name: 'Mongolian' },
    { code: 'ka-GE', name: 'Georgian' },
    { code: 'hy-AM', name: 'Armenian' },
    { code: 'kk-KZ', name: 'Kazakh' },
    { code: 'uz-UZ', name: 'Uzbek' },
    { code: 'az-AZ', name: 'Azerbaijani' },
    { code: 'ps-AF', name: 'Pashto' },
    { code: 'tk-TM', name: 'Turkmen' },
];

interface FormProps {
    setScreen: (screen: 'intro' | 'recorder' | 'uploader' | 'webcam' | 'form') => void;
}


// Utility function to validate CSS hex color
const isValidHexColor = (color: string): boolean => {
    return /^#([0-9A-F]{3}){1,2}$/i.test(color);
};

const Form: React.FC<FormProps> = ({ setScreen }) => {
    const { msp, account } = useParams();
    const { layout } = useLayout();
    const { recordedBlob, uploadedFiles, sampleDuration, setRecordedBlob, setUploadedFiles, setSampleDuration } = useSample();
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [language, setLanguage] = useState<string>('');
    const [accent, setAccent] = useState<string>('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errors, setErrors] = useState<{ name?: string; email?: string; language?: string; image?: string }>({});
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listDivRef = useRef<HTMLDivElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);

    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : '#22FFAF',
        text: isValidHexColor(layout.color.text) ? layout.color.text : '#000000',
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : '#1DE9A3',
    };

    const filteredLanguages = languages.filter((lang) => lang.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleLanguageSelect = (lang: { code: string; name: string }) => {
        setLanguage(lang.name);
        setSearchQuery(lang.name);
        setIsDropdownOpen(false);
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0];
            const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];
            const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif'];

            const extension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
                setErrors((prev) => ({
                    ...prev,
                    image: 'Please select a valid image file (PNG, JPEG, GIF)',
                }));
                setImageFile(null);
                if (imageInputRef.current) {
                    imageInputRef.current.value = '';
                }
                return;
            }

            setErrors((prev) => ({ ...prev, image: undefined }));
            setImageFile(file);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        if (imageInputRef.current) {
            imageInputRef.current.value = '';
        }
    };

    const validateForm = () => {
        const newErrors: { name?: string; email?: string; language?: string } = {};
        let isValid = true;

        if (!name.trim()) {
            newErrors.name = 'Name is required';
            isValid = false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim()) {
            newErrors.email = 'Email is required';
            isValid = false;
        } else if (!emailRegex.test(email)) {
            newErrors.email = 'Please enter a valid email';
            isValid = false;
        }

        if (!language.trim() || !languages.some((lang) => lang.name === language)) {
            newErrors.language = 'Please select a valid language';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        validateForm();
    }, [name, email, language]);

    const handleSubmit = async () => {
        if (!validateForm()) {
            alert('Please fill in all required fields correctly.');
            return;
        }

        if (!recordedBlob && (!uploadedFiles || uploadedFiles.length === 0)) {
            alert('No audio or video sample found. Please record or upload a sample.');
            return;
        }

        if (sampleDuration < 60) {
            alert('Sample duration must be at least 60 seconds.');
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            if (recordedBlob) {
                const isVideo = recordedBlob.type.startsWith('video/');
                formData.append('audio', recordedBlob, isVideo ? 'recording.webm' : 'recording.wav');
            }
            if (uploadedFiles && uploadedFiles.length > 0) {
                uploadedFiles.forEach((file, index) => {
                    const isVideo = file.type.startsWith('video/');
                    formData.append('audio', file, isVideo ? `upload-${index}.webm` : file.name);
                });
            }
            if (imageFile) {
                formData.append('image', imageFile, imageFile.name);
            }
            formData.append('name', name);
            formData.append('msp', msp || 'Not provided');
            formData.append('account', account || 'Not provided');
            formData.append('email', email);
            formData.append('language', language);
            formData.append('accent', accent || 'Not specified');

            console.log('FormData entries:', [...formData.entries()]);

            const response = await fetch('/send-email', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }
            const result = await response.json();
            alert('Submission successful! Files and form data sent.');
            setScreen('intro');
            setRecordedBlob(null);
            setUploadedFiles(null);
            setSampleDuration(0);
            setName('');
            setEmail('');
            setLanguage('');
            setAccent('');
            setImageFile(null);
        } catch (error) {
            console.error('Submission error:', error);
            alert(`Failed to submit: ${error.message || 'Please check the server or try again.'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                searchInputRef.current &&
                listDivRef.current &&
                !searchInputRef.current.contains(e.target as Node) &&
                !listDivRef.current.contains(e.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    return (
        <div
            className={`w-full max-w-[375px] p-6 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center`}
        >
            <h1 className={`text-[30px] font-bold text-[${safeColors.buttons}] mb-5`}>Submit Sample</h1>
            <div className="mb-4 w-full">
                <label className="block text-sm text-white font-medium">Your Name*</label>
                <input
                    type="text"
                    className={`mt-1 p-2 w-full border border-solid rounded-md bg-[#313131] text-[white] placeholder:text-gray-400 border-[${safeColors.buttons}] ${errors.name ? 'border-red-500' : ''}`}
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </div>
            <div className="mb-4 w-full">
                <label className="block text-sm text-white font-medium">Your Email*</label>
                <input
                    type="email"
                    className={`mt-1 p-2 w-full border border-solid rounded-md bg-[#313131] text-[white] placeholder:text-gray-400 border-[${safeColors.buttons}] ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </div>
            <div className="mb-4 w-full relative">
                <label className="block text-sm text-white font-medium">Your Language*</label>
                <input
                    type="text"
                    ref={searchInputRef}
                    className={`mt-1 p-2 w-full bg-[#313131] text-[white] placeholder:text-gray-400 border border-solid rounded-md border-[${safeColors.buttons}] ${isDropdownOpen && filteredLanguages.length > 0 ? 'rounded-t-md' : 'rounded-md'} ${errors.language ? 'border-red-500' : ''}`}
                    placeholder="Type to search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setIsDropdownOpen(true)}
                />
                <div
                    ref={listDivRef}
                    className={`absolute w-full max-h-40 overflow-y-auto bg-[#1A1A1A] text-white border border-[${safeColors.buttons}] border-t-0 rounded-b-md ${isDropdownOpen && filteredLanguages.length > 0 ? 'block' : 'hidden'}`}
                >
                    {filteredLanguages.map((lang) => (
                        <div
                            key={lang.code}
                            className={`px-2 py-1 cursor-pointer hover:bg-[${safeColors.buttons}] hover:text-[${safeColors.text}]`}
                            onClick={() => handleLanguageSelect(lang)}
                        >
                            {lang.name}
                        </div>
                    ))}
                </div>
            </div>
            <div className="mb-4 w-full">
                <label className="block text-sm text-white font-medium">Your Accent</label>
                <input
                    className={`mt-1 p-2 w-full border border-solid rounded-md bg-[#313131] text-[white] placeholder:text-gray-400 border-[${safeColors.buttons}]`}
                    placeholder="Enter your accent, if applicable"
                    value={accent}
                    onChange={(e) => setAccent(e.target.value)}
                />
            </div>
            <div className="mb-4 w-full">
                <label className="block text-sm text-white font-medium">Your Image (Optional)</label>
                <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
                    className={`mt-1 p-2 w-full border border-solid rounded-md bg-[${safeColors.buttons}] text-[${safeColors.text}] placeholder:text-gray-400 border-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] cursor-pointer`}
                    onChange={handleImageChange}
                    ref={imageInputRef}
                />
                {imageFile && (
                    <div className="flex justify-between items-center text-white text-sm mt-2">
                        <span>
                            {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                        </span>
                        <button className="text-red-500 hover:text-red-700" onClick={handleRemoveImage}>
                            Remove
                        </button>
                    </div>
                )}
            </div>
            {sampleDuration < 60 && (
                <label className="block text-sm text-red-500 font-medium mb-[24px]">
                    Your sample is less than a minute long, please record or upload another sample
                </label>
            )}
            <div className="flex gap-[60px]">
                <button
                    className="bg-[#313131] hover:bg-[#5a5a5a] border-none rounded-lg px-4 py-[11px] font-bold flex items-center text-white gap-3 text-base cursor-pointer"
                    onClick={() => {
                        setScreen('intro');
                        setRecordedBlob(null);
                        setUploadedFiles(null);
                        setSampleDuration(0);
                        setName('');
                        setEmail('');
                        setLanguage('');
                        setAccent('');
                        setImageFile(null);
                    }}
                >
                    Start over
                </button>
                <button
                    className={`border-none rounded-lg px-4 py-[11px] font-bold flex items-center gap-3 text-base bg-[${safeColors.buttons}] hover:bg-[${safeColors.hover}] text-[${safeColors.text}] ${isSubmitting || errors.name || errors.email || errors.language || sampleDuration < 60 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={handleSubmit}
                    disabled={!!errors.name || !!errors.email || !!errors.language || isSubmitting || sampleDuration < 60}
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default Form;